// apps/web/src/hooks/useIdlePrompt.ts
import { useEffect, useRef, useState } from 'react';

export interface UseIdlePromptOptions {
  enabled: boolean;
  idleMs: number;
  promptMs: number;
  onTimeout: () => void;
  onDismissExtra?: () => void;
}

export interface UseIdlePromptResult {
  prompting: boolean;
  remainingMs: number;
  dismiss: () => void;
}

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'mousemove', 'wheel'] as const;
const IDLE_CHECK_INTERVAL_MS = 60_000;
const COUNTDOWN_TICK_MS = 1_000;

export function useIdlePrompt(opts: UseIdlePromptOptions): UseIdlePromptResult {
  const { enabled, idleMs, promptMs, onTimeout, onDismissExtra } = opts;
  const [prompting, setPrompting] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);

  const lastActivityRef = useRef<number>(Date.now());
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const onDismissExtraRef = useRef(onDismissExtra);
  onDismissExtraRef.current = onDismissExtra;

  const dismissRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!enabled) {
      setPrompting(false);
      setRemainingMs(0);
      return;
    }

    lastActivityRef.current = Date.now();
    let promptStartedAt = 0;
    let pollId: ReturnType<typeof setInterval> | null = null;
    let countdownId: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const clearPromptTimers = () => {
      if (countdownId !== null) { clearInterval(countdownId); countdownId = null; }
      if (timeoutId !== null)   { clearTimeout(timeoutId);   timeoutId   = null; }
    };

    const enterPrompting = () => {
      promptStartedAt = Date.now();
      setPrompting(true);
      setRemainingMs(promptMs);
      countdownId = setInterval(() => {
        const elapsed = Date.now() - promptStartedAt;
        setRemainingMs(Math.max(0, promptMs - elapsed));
      }, COUNTDOWN_TICK_MS);
      timeoutId = setTimeout(() => {
        clearPromptTimers();
        onTimeoutRef.current();
      }, promptMs);
    };

    const onActivity = () => { lastActivityRef.current = Date.now(); };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }

    pollId = setInterval(() => {
      if (Date.now() - lastActivityRef.current > idleMs) {
        if (countdownId === null && timeoutId === null) enterPrompting();
      }
    }, IDLE_CHECK_INTERVAL_MS);

    dismissRef.current = () => {
      clearPromptTimers();
      lastActivityRef.current = Date.now();
      setPrompting(false);
      setRemainingMs(0);
      onDismissExtraRef.current?.();
    };

    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity);
      if (pollId !== null) clearInterval(pollId);
      clearPromptTimers();
      dismissRef.current = () => {};
    };
  }, [enabled, idleMs, promptMs]);

  return {
    prompting,
    remainingMs,
    dismiss: () => dismissRef.current(),
  };
}
